var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from "react";
import { Bar as BarChart } from "react-chartjs-2";
import isEqual from "lodash/isEqual";
import "./RoundedBars.js";
const Graph = (_a) => {
    var { className, labels, datasets } = _a, props = __rest(_a, ["className", "labels", "datasets"]);
    const [innerLabels, setInnerLabels] = useState(labels);
    const [areLabelsSplit, setAreLabelsSplit] = useState(false);
    const [lastResizedWidth, setLastResizedWidth] = useState(0);
    const splitLabels = (lastWidth) => {
        const newLabels = labels.map((item) => {
            const splitArray = item.split("-");
            if (splitArray.length > 1) {
                splitArray[0] += "-";
            }
            return splitArray.map(item => item.trim());
        });
        if (!isEqual(innerLabels, newLabels)) {
            setInnerLabels(newLabels);
            setAreLabelsSplit(true);
            setLastResizedWidth(lastWidth);
        }
    };
    const restoreLabels = (lastWidth) => {
        if (!isEqual(innerLabels, labels)) {
            setInnerLabels(labels);
            setAreLabelsSplit(false);
            setLastResizedWidth(lastWidth);
        }
    };
    useEffect(() => {
        // Reset to original labels and non-split state when the prop changes
        setInnerLabels(labels);
        setAreLabelsSplit(false);
        setLastResizedWidth(0); // Reset width memory too
    }, [labels]); // This effect runs when the 'labels' prop changes
    const options = {
        title: { display: false },
        legend: {
            display: datasets.length > 1,
            align: "end",
            reverse: true,
            labels: {
                fontColor: "#00264C",
                fontSize: 13,
                fontStyle: "600",
                boxWidth: 12,
                padding: 25,
            },
        },
        tooltips: {
            enabled: true,
            callbacks: {
                label: function (tooltipItem, _data) {
                    const value = Number(tooltipItem.value);
                    if (!value || isNaN(value)) {
                        return "";
                    }
                    return value.toLocaleString("cs-CZ", { style: "decimal", maximumFractionDigits: 0 });
                },
            },
        },
        events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
        hover: { mode: "index" },
        cornerRadius: 5,
        showLines: false,
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [
                {
                    ticks: {
                        maxRotation: 0,
                    },
                    gridLines: { display: false },
                    stacked: true,
                    afterUpdate: (scale) => {
                        const currentWidth = scale.width;
                        if (Math.abs(currentWidth - lastResizedWidth) > 5) {
                            const columnWidth = scale.maxWidth / scale.ticks.length;
                            const isTooWide = scale.longestLabelWidth > columnWidth * 0.95;
                            const isTooNarrow = scale.longestLabelWidth < columnWidth / 2.05;
                            setTimeout(() => {
                                // Only split if not currently split AND too wide
                                if (!areLabelsSplit && isTooWide) {
                                    splitLabels(currentWidth);
                                    // Only restore if currently split AND narrow enough
                                }
                                else if (areLabelsSplit && isTooNarrow) {
                                    restoreLabels(currentWidth);
                                }
                                else if (currentWidth !== lastResizedWidth) {
                                    setLastResizedWidth(currentWidth);
                                }
                            }, 1);
                        }
                    },
                },
            ],
            yAxes: [
                {
                    gridLines: {
                        drawTicks: false,
                        lineWidth: 1,
                        color: "#DFE0E2",
                        zeroLineColor: "#DFE0E2",
                        drawBorder: false,
                    },
                    ticks: {
                        beginAtZero: true,
                        padding: 10,
                        callback: (value, _index, _values) => {
                            return value.toLocaleString("cs-CZ", { style: "decimal", maximumFractionDigits: 0 });
                        },
                    },
                    stacked: true,
                },
            ],
        },
    };
    const graphData = (canvas) => {
        const ctx = canvas.getContext("2d");
        const offsetAxisTop = 10;
        const offsetAxisBottom = 32;
        const offsetTop = canvas.height - offsetAxisBottom;
        const dataNumbers = datasets[0].data; // @TODO: Why number[] is needed when typed above?
        const maxValue = Math.max(...dataNumbers);
        let colors = [];
        if (datasets.length === 1 && ctx) {
            colors = datasets[0].data.map((val) => {
                const offsetRatio = maxValue !== 0 ? val / maxValue : maxValue;
                const gradient = ctx.createLinearGradient(0, offsetAxisTop + (offsetTop - offsetTop * offsetRatio), 0, offsetTop);
                // In a case that further development will be needed, I'm leaving this here just for better debugging of gradient start/stop postition...
                // gradient.addColorStop(0, "red");
                // gradient.addColorStop(0.1, "red");
                // gradient.addColorStop(0.11, "blue");
                // gradient.addColorStop(0.89, "blue");
                // gradient.addColorStop(0.9, "red");
                // gradient.addColorStop(1, "red");
                gradient.addColorStop(0, "#00C34C");
                gradient.addColorStop(1, "#D1FFD8");
                return gradient;
            });
        }
        else {
            colors = ["#00264C", "#00C34C"];
        }
        const datasetDefault = {
            backgroundColor: colors,
            borderWidth: 0,
            barPercentage: 1,
            maxBarThickness: 140,
        };
        const chartDataset = [];
        if (datasets.length === 1) {
            chartDataset.push(Object.assign(Object.assign({}, datasetDefault), datasets[0]));
        }
        else {
            // Following ignores are due to bad typing which is currently "string[] | CanvasGradient[]", but should be "string | CanvasGradient[]" by Chart.js documentation
            // @ts-ignore
            datasetDefault.backgroundColor = colors[0];
            chartDataset.push(Object.assign(Object.assign(Object.assign({}, datasetDefault), datasets[0]), { borderSkipped: "top" })); // borderSkipped === "top" is here used for disable border-radius generation for bottom part of bar
            // @ts-ignore
            datasetDefault.backgroundColor = colors[1];
            chartDataset.push(Object.assign(Object.assign({}, datasetDefault), datasets[1]));
        }
        return {
            labels: innerLabels,
            datasets: chartDataset,
        };
    };
    return (React.createElement("div", { className: className },
        React.createElement(BarChart, { width: 450, height: 260, data: graphData, options: options })));
};
export default Graph;
//# sourceMappingURL=index.js.map